import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCensusBeds, getBedOccupants } from "../api/bedCensusApi";

function calcAge(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

function normalizeCensusAdmissions(rows) {
  return (rows || []).map((item) => ({
    admissionId: item?.admission_id ?? null,
    patientName: item?.patient?.name || "—",
    patientId: item?.patient?.patient_id || "",
    uhid: item?.patient?.uhid || "",
    age: calcAge(item?.patient?.dob),
    gender: item?.patient?.gender || "",
    // No invented fallback here — this hook only ever fetches
    // status=Allocated rows (see getBedOccupants), so a missing status on
    // the response would be a real data anomaly worth surfacing as-is
    // rather than masking with a guessed value.
    status: item?.status ?? null,
    bedCode: item?.bed_id || "—",
    bedTypeName: item?.bed_type_name || "",
    admittedAt: item?.admitted_at || null,
    consultantName: item?.doctors?.admitting?.name || "—",
    payerType: item?.admission_details?.payer_type || "—",
    // Server-computed, read-only (Task B2/backend) — absent on admissions
    // created before this feature, and until other_fields round-trips on
    // this GET (separate backend task), so the "—" fallback is expected.
    admissionSource: item?.other_fields?.admission_source || "—",
  }));
}

const EMPTY_FILTERS = {
  status: "",
  room_category: "",
  room: "",
};

// Shared data hook for the bed census/dashboard (ADR 0001 epic), consumed by
// both BedAllocation.js and the nurse dashboard's BedDashboard.js. All filters
// are applied server-side by ipd_bed/ (see manage_bed.py).
//
// HIERARCHY: Ward > Room > Bed. A bed no longer belongs to a department at all,
// so the census filters on physical location and status only — department
// filtering belongs on the patient lists, which filter the ADMISSION's
// department.
//
// The bed payload's `ward_code`/`ward_name` do NOT hold the ward this screen
// means — the Ward rows in this tenant hold DEPARTMENT names ("Orthopaedics",
// "Cardiology"). The ward shown here is `room_category` (ICU / General / ITU /
// Private / Semi-Private), and ward_code/ward_name are deliberately never read.
// Note this differs from the admission wizard and the transfer picker, where a
// ward is the BED TYPE; converging the two is deliberately out of scope.
export default function useBedCensusData() {
  const [beds, setBeds] = useState([]);
  const [admissionByBed, setAdmissionByBed] = useState({});
  const [loading, setLoading] = useState(true);
  const [filters, setFiltersState] = useState(EMPTY_FILTERS);
  // Patient search — client-side only (admissionByBed is already fully
  // loaded), so it doesn't trigger a reload() the way the other filters do.
  const [search, setSearch] = useState("");
  // Guards against out-of-order responses: if filters change again before an
  // in-flight reload() resolves, that stale response is discarded instead of
  // overwriting state with data that no longer matches the active filters.
  const reloadSeq = useRef(0);

  const reload = useCallback(async () => {
    const seq = ++reloadSeq.current;
    setLoading(true);
    try {
      const [bedRows, admissionRows] = await Promise.all([
        getCensusBeds(filters),
        getBedOccupants(),
      ]);
      if (seq !== reloadSeq.current) return;
      setBeds(bedRows);
      const map = {};
      normalizeCensusAdmissions(admissionRows).forEach((a) => {
        if (a.bedCode && a.bedCode !== "—") map[a.bedCode] = a;
      });
      setAdmissionByBed(map);
    } finally {
      if (seq === reloadSeq.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.room_category, filters.room]);

  useEffect(() => {
    reload();
  }, [reload]);

  const setFilters = useCallback((patch) => {
    setFiltersState((prev) => {
      const next = { ...prev, ...patch };
      // Ward > Room: changing a level invalidates everything below it.
      if ("room_category" in patch) {
        next.room = "";
      }
      return next;
    });
  }, []);

  // Every ward and room seen in any load, keyed by code. A facet's options must
  // not come from beds that facet has already filtered: picking a ward left
  // only that ward in the dropdown, and picking a room only that room (QA,
  // 2026-09-24). The first load is unfiltered, so it sees them all; they also
  // survive an over-narrowed combination (e.g. a Ward + Status with zero
  // matching beds) that would otherwise empty every dropdown.
  const seenRef = useRef({ wards: new Map(), rooms: new Map() });

  // The ward list — sourced from room_category, which is where ICU / General /
  // ITU / Private actually live (see the hierarchy note at the top). The
  // matching filter key stays `room_category`, because that is what ipd_bed/
  // accepts; only the meaning we attach to it changes.
  const wards = useMemo(() => {
    const seen = seenRef.current.wards;
    beds.forEach((b) => {
      if (b.room_category_code && !seen.has(b.room_category_code)) {
        seen.set(b.room_category_code, b.room_category_name || b.room_category_code);
      }
    });
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [beds]);

  // Ward > Room: the rooms of the chosen ward, or every room when none is.
  const rooms = useMemo(() => {
    const seen = seenRef.current.rooms;
    beds.forEach((b) => {
      if (b.room_code && !seen.has(b.room_code)) {
        seen.set(b.room_code, { name: b.room_name || b.room_code, ward: b.room_category_code });
      }
    });
    return Array.from(seen)
      .filter(([, room]) => !filters.room_category || room.ward === filters.room_category)
      .map(([code, room]) => ({ code, name: room.name }));
  }, [beds, filters.room_category]);

  // Matches the bed's code / name / bed type code (vacant beds included), or
  // the occupant's name / UHID / patient ID / bed type name. Ward/room option
  // lists above are deliberately derived from the unfiltered `beds`, so
  // searching never makes the other dropdowns look emptier than they are.
  const displayBeds = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return beds;
    return beds.filter((b) => {
      const a = admissionByBed[b.code] || {};
      return [b.code, b.name, b.bed_type_code, a.patientName, a.uhid, a.patientId, a.bedTypeName].some(
        (v) => String(v || "").toLowerCase().includes(q)
      );
    });
  }, [beds, admissionByBed, search]);

  return {
    beds: displayBeds,
    admissionByBed,
    loading,
    reload,
    filters,
    setFilters,
    search,
    setSearch,
    wards,
    rooms,
  };
}
